import { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { LayoutDashboard, Car, FileCheck2, KeyRound, User2, Sparkles, CalendarCheck, Wallet, ArrowRight, Upload, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { CATEGORY_LABELS, PLAN_LABELS, STATUS_LABELS, currency, formatDate } from "@/lib/format";

const statusBadge = (s) => {
  if (s === "confirmed" || s === "completed") return { cls: "badge-availability", icon: CheckCircle2 };
  if (s === "rejected" || s === "cancelled") return { cls: "badge-danger", icon: XCircle };
  return { cls: "badge-warning", icon: Clock };
};

export default function CustomerPortalPage() {
  const { user, refreshMe, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [membership, setMembership] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: "", phone: "" });
  const [uploadForm, setUploadForm] = useState({ doc_type: "drivers_license", file_name: "", notes: "" });
  const [uploadFile, setUploadFile] = useState(null);

  const activeTab = params.get("tab") || "dashboard";
  const setTab = (t) => { const next = new URLSearchParams(params); next.set("tab", t); setParams(next, { replace: true }); };

  const load = async () => {
    setLoading(true);
    try {
      const [b, m, d] = await Promise.all([
        axios.get(`${API}/bookings/mine`),
        axios.get(`${API}/membership/mine`),
        axios.get(`${API}/documents/mine`),
      ]);
      setBookings(b.data || []);
      setMembership(m.data || null);
      setDocuments(d.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => { if (user) setProfileForm({ full_name: user.full_name || "", phone: user.phone || "" }); }, [user]);

  const join = async () => {
    setJoining(true);
    try {
      await axios.post(`${API}/membership/join`, { payment_method: "card_placeholder" });
      const me = await refreshMe();
      if (me) setUser(me);
      await load();
      toast.success("Welcome to Drive Alfa Club!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not join membership"); }
    finally { setJoining(false); }
  };

  const uploadDoc = async () => {
    if (!uploadFile) { toast.error("Please choose a file"); return; }
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result;
          await axios.post(`${API}/documents`, {
            doc_type: uploadForm.doc_type,
            file_name: uploadForm.file_name || uploadFile.name,
            data_url: dataUrl,
            notes: uploadForm.notes,
          });
          toast.success("Document uploaded");
          setUploadFile(null);
          setUploadForm({ doc_type: "drivers_license", file_name: "", notes: "" });
          await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
      };
      reader.readAsDataURL(uploadFile);
    } catch (e) { toast.error("Upload failed"); }
  };

  const saveProfile = async () => {
    try {
      const res = await axios.patch(`${API}/auth/me`, profileForm);
      setUser(res.data);
      localStorage.setItem("ad_user", JSON.stringify(res.data));
      toast.success("Profile updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const cancelBooking = async (id) => {
    try {
      await axios.post(`${API}/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      await load();
    } catch (e) { toast.error("Cancel failed"); }
  };

  if (!user) return <Navigate to="/login?next=/portal" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  const tabs = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "bookings", label: "My Bookings", icon: Car },
    { key: "documents", label: "Documents", icon: FileCheck2 },
    { key: "membership", label: "Membership", icon: KeyRound },
    { key: "profile", label: "Profile", icon: User2 },
  ];

  const upcoming = bookings.filter(b => b.status !== "cancelled" && b.status !== "rejected" && b.status !== "completed").slice(0,3);
  const stats = {
    total: bookings.length,
    active: bookings.filter(b => b.status === "confirmed").length,
    pending: bookings.filter(b => b.status === "pending").length,
  };

  return (
    <PortalLayout tabs={tabs} activeTab={activeTab} onTabChange={setTab} kind="customer">
      {activeTab === "dashboard" && (
        <div data-testid="customer-dashboard">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Car, k: "Bookings", v: stats.total, sub: `${stats.active} confirmed · ${stats.pending} pending` },
              { icon: Sparkles, k: "Membership", v: user.is_member ? "Active" : "Not joined", sub: user.is_member ? "Drive Alfa Club" : "Join to unlock savings" },
              { icon: Wallet, k: "Estimated saved", v: currency(bookings.filter(b => b.is_member_pricing).reduce((a,b) => a + (b.savings || 0), 0)), sub: "From member pricing" },
            ].map((s, i) => (
              <div key={i} className="card-clean p-5">
                <s.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground">{s.k}</div>
                <div className="font-display text-2xl font-semibold mt-1" data-testid={`customer-stat-${s.k.toLowerCase().replace(/\s+/g,'-')}`}>{s.v}</div>
                <div className="text-xs text-muted-foreground">{s.sub}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <div className="card-clean p-5 lg:col-span-2">
              <div className="flex items-center justify-between">
                <div className="font-display text-lg font-semibold">Upcoming bookings</div>
                <Button asChild size="sm" variant="outline" className="rounded-full"><button onClick={() => setTab("bookings")}>View all <ArrowRight className="h-3.5 w-3.5 ml-1" /></button></Button>
              </div>
              {upcoming.length === 0 ? (
                <div className="mt-4 text-sm text-muted-foreground">No upcoming bookings. <Link to="/vehicles" className="underline">Browse vehicles</Link>.</div>
              ) : (
                <div className="mt-4 flex flex-col gap-3">
                  {upcoming.map((b) => {
                    const s = statusBadge(b.status);
                    return (
                      <div key={b.id} className="flex items-center gap-3 border border-border rounded-xl p-3">
                        <img src={b.vehicle_image} alt={b.vehicle_name} className="h-14 w-20 rounded-lg object-cover" />
                        <div className="flex-1">
                          <div className="font-medium">{b.vehicle_name}</div>
                          <div className="text-xs text-muted-foreground">{formatDate(b.start_date)} → {formatDate(b.end_date)} · {PLAN_LABELS[b.plan_type]}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1 rounded-full text-[11px] px-2 py-1 ${s.cls}`}><s.icon className="h-3 w-3" /> {STATUS_LABELS[b.status]}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="card-clean p-5" data-testid="customer-membership-status">
              <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-[hsl(var(--primary))]" /> <div className="font-display text-lg font-semibold">Membership</div></div>
              {user.is_member ? (
                <>
                  <div className="mt-3">
                    <div className="badge-blue inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs"><Sparkles className="h-3 w-3" /> Active member</div>
                    <div className="mt-3 text-sm text-muted-foreground">Ends: {membership ? formatDate(membership.end_date) : "—"}</div>
                  </div>
                  <div className="mt-4 text-sm text-muted-foreground">Enjoy member pricing, Rental Term Leasing access, and business options.</div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mt-2">Unlock member pricing on every rental — save up to $880 per booking.</p>
                  <Button className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] mt-3" onClick={join} disabled={joining} data-testid="portal-join-club">{joining ? "Processing…" : "Join Drive Alfa Club"}</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "bookings" && (
        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-semibold">My bookings</h2>
            <Button asChild className="rounded-xl bg-black hover:bg-black/90"><Link to="/vehicles">New reservation</Link></Button>
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground mt-6">Loading…</div>
          ) : bookings.length === 0 ? (
            <div className="card-clean p-8 mt-6 text-center">
              <p className="text-muted-foreground">No bookings yet.</p>
              <Button asChild className="mt-4 rounded-xl bg-black hover:bg-black/90"><Link to="/vehicles">Browse vehicles</Link></Button>
            </div>
          ) : (
            <div className="card-clean mt-6 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((b) => {
                    const s = statusBadge(b.status);
                    return (
                      <TableRow key={b.id}>
                        <TableCell className="font-mono text-xs">{b.booking_ref}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <img src={b.vehicle_image} alt={b.vehicle_name} className="h-10 w-14 rounded-md object-cover" />
                            <div>
                              <div className="text-sm font-medium">{b.vehicle_name}</div>
                              <div className="text-xs text-muted-foreground">{CATEGORY_LABELS[b.vehicle_category] || b.vehicle_category}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{formatDate(b.start_date)}<br />{formatDate(b.end_date)}</TableCell>
                        <TableCell className="text-xs">{PLAN_LABELS[b.plan_type]}</TableCell>
                        <TableCell className="text-sm">
                          <div>{currency(b.is_member_pricing ? b.member_total : b.standard_total)}</div>
                          {b.is_member_pricing && <div className="text-[11px] text-[hsl(var(--primary))]">Saved {currency(b.savings)}</div>}
                        </TableCell>
                        <TableCell><span className={`inline-flex items-center gap-1 rounded-full text-[11px] px-2 py-1 ${s.cls}`}><s.icon className="h-3 w-3" /> {STATUS_LABELS[b.status]}</span></TableCell>
                        <TableCell>
                          {(b.status === "pending" || b.status === "confirmed") && (
                            <Button size="sm" variant="ghost" onClick={() => cancelBooking(b.id)} data-testid={`cancel-${b.id}`}>Cancel</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {activeTab === "documents" && (
        <div>
          <h2 className="font-display text-2xl font-semibold">Documents</h2>
          <p className="text-sm text-muted-foreground">Upload your driver’s licence and any additional documents required for your booking.</p>
          <div className="grid lg:grid-cols-2 gap-4 mt-6">
            <div className="card-clean p-5">
              <div className="font-display text-lg font-semibold">Upload a document</div>
              <div className="mt-4 grid gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={uploadForm.doc_type} onValueChange={(v) => setUploadForm({ ...uploadForm, doc_type: v })}>
                    <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="drivers_license">Driver’s licence</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                      <SelectItem value="business_reg">Business registration</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>File</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="doc-file" />
                </div>
                <div>
                  <Label>File name (optional)</Label>
                  <Input value={uploadForm.file_name} onChange={(e) => setUploadForm({ ...uploadForm, file_name: e.target.value })} placeholder="e.g. drivers_license_front.jpg" />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input value={uploadForm.notes} onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })} />
                </div>
                <Button className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" onClick={uploadDoc} data-testid="doc-upload-submit"><Upload className="h-4 w-4 mr-1" /> Upload</Button>
              </div>
            </div>
            <div className="card-clean p-5">
              <div className="font-display text-lg font-semibold">Your documents</div>
              {documents.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-3">No documents uploaded yet.</p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {documents.map((d) => (
                    <li key={d.id} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{d.file_name}</div>
                        <div className="text-xs text-muted-foreground">{d.doc_type}</div>
                      </div>
                      <span className="text-[11px] rounded-full px-2 py-1 badge-blue">{d.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "membership" && (
        <div>
          <h2 className="font-display text-2xl font-semibold">Drive Alfa Club</h2>
          <p className="text-sm text-muted-foreground">Premium access. Better pricing. Longer-term vehicle freedom.</p>
          <div className="grid lg:grid-cols-2 gap-4 mt-6">
            <div className="card-clean p-6">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Your status</div>
              {user.is_member ? (
                <>
                  <div className="mt-2 inline-flex items-center gap-1 badge-blue rounded-full px-3 py-1 text-xs"><Sparkles className="h-3 w-3" /> Active member</div>
                  {membership && (
                    <div className="mt-4 text-sm text-muted-foreground">Renews on {formatDate(membership.end_date)} · CAA active · IPTV active</div>
                  )}
                </>
              ) : (
                <>
                  <div className="mt-2 font-display text-2xl font-semibold">Not a member yet</div>
                  <p className="text-sm text-muted-foreground mt-2">Unlock member pricing, Rental Term Leasing, CAA membership, and more.</p>
                  <Button className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] mt-4" onClick={join} disabled={joining} data-testid="membership-tab-join">{joining ? "Processing…" : "Join for $1,000/year"}</Button>
                </>
              )}
            </div>
            <div className="card-clean p-6">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Included</div>
              <ul className="mt-3 text-sm space-y-2">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> Member rental pricing (up to $880 per booking)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> Access to Rental Term Leasing (3, 6, 12 months)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> 2 free rental days per month (with 4+ day rentals)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> 1-year CAA + IPTV bonus</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> Alfa One Path business access</li>
              </ul>
              <Separator className="my-4" />
              <Button asChild variant="outline" className="rounded-xl"><Link to="/drive-alfa-club">View full benefits page</Link></Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "profile" && (
        <div>
          <h2 className="font-display text-2xl font-semibold">Profile</h2>
          <div className="card-clean p-6 mt-6 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full name</Label>
                <Input value={profileForm.full_name} onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })} data-testid="profile-name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} data-testid="profile-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={user.email} disabled />
              </div>
              <div>
                <Label>Role</Label>
                <Input value={user.role} disabled />
              </div>
            </div>
            <Button className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] mt-5" onClick={saveProfile} data-testid="profile-save">Save changes</Button>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
